import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import type { JobFileEntry } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useJobFiles } from '@/hooks/useJobFiles';
import { isImageEntry } from './jobFileDisplay';
import { JobFileThumbnail } from './JobFileThumbnail';
import { JobFolderNavigator } from './JobFolderNavigator';

// Choosing photos out of the ones already on the job.
//
// The whole reason the file store exists: someone photographed the site from a
// phone, and what gets built from those photos — a report page, an expense
// receipt — should be buildable without going back to the phone. Two consumers
// share this dialog; what they mean by a pick differs, so the wording they need
// arrives as props rather than being written in here twice.
//
// It navigates folders exactly as the Files tab does, so a photo is where the
// person who took it put it. Non-images are shown but not selectable: hiding
// them would make the folder look emptier than it is.

interface JobPhotoPickerDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Which photos are already spoken for, and what to call it. Marked rather
   * than hidden: the same photo legitimately serves twice — cropped differently
   * on two report pages, or backing a second expense.
   *
   * Both are needed for a badge to appear; a predicate with nothing to say is
   * the default.
   */
  isUsed?: (file: JobFileEntry) => boolean;
  usedLabel?: string;
  /** The sentence under the title. Falls back to a neutral one. */
  description?: string;
  /** Places there are to fill. The selection can't outgrow it. */
  maxSelection: number;
  onConfirm: (entries: JobFileEntry[]) => void;
}

export function JobPhotoPickerDialog({
  jobId,
  open,
  onOpenChange,
  isUsed,
  usedLabel,
  description,
  maxSelection,
  onConfirm,
}: JobPhotoPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        {/* Remounted per opening, so a previous pick's search and selection
            don't greet the next one. */}
        {open && (
          <PickerBody
            jobId={jobId}
            isUsed={isUsed}
            usedLabel={usedLabel}
            description={description}
            maxSelection={maxSelection}
            onConfirm={(entries) => {
              onConfirm(entries);
              onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function matches(file: JobFileEntry, needle: string): boolean {
  return (
    file.fileName.toLowerCase().includes(needle) ||
    (file.description?.toLowerCase().includes(needle) ?? false) ||
    file.tags.some((tag) => tag.toLowerCase().includes(needle))
  );
}

function PickerBody({
  jobId,
  isUsed,
  usedLabel,
  description,
  maxSelection,
  onConfirm,
  onCancel,
}: {
  jobId: string;
  isUsed?: (file: JobFileEntry) => boolean;
  usedLabel?: string;
  description?: string;
  maxSelection: number;
  onConfirm: (entries: JobFileEntry[]) => void;
  onCancel: () => void;
}) {
  // The open folder, or null for the job root — the same navigation the Files
  // tab does, so a photo is where the person who took it put it.
  const [folderId, setFolderId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // The chosen entries themselves, in pick order — that order is what decides
  // which photo lands in which slot.
  //
  // Entries and not ids: only the open folder's files are loaded at any moment,
  // so an id picked in one folder could not be resolved back to a file after
  // navigating to another, and the selection was silently dropped on confirm.
  // Holding the entry means a pick survives wherever the user goes next.
  const [picked, setPicked] = useState<JobFileEntry[]>([]);

  const searching = search.trim().length > 0;
  // A search asks about the job, not the open folder, matching the Files tab.
  const { data, isLoading } = useJobFiles(jobId, searching ? {} : { folderId });

  const files = useMemo(() => data ?? [], [data]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? files.filter((file) => matches(file, needle)) : files;
  }, [files, search]);

  const toggle = (file: JobFileEntry) =>
    setPicked((prev) =>
      prev.some((entry) => entry.id === file.id)
        ? prev.filter((entry) => entry.id !== file.id)
        : prev.length >= maxSelection
          ? prev
          : [...prev, file],
    );

  const atCapacity = picked.length >= maxSelection;
  const pickedIndex = (id: string) => picked.findIndex((entry) => entry.id === id);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Choose from job photos</DialogTitle>
        <DialogDescription>
          {description ??
            (maxSelection > 1
              ? `Photos already on this job. Pick up to ${maxSelection} — they are used in the order you pick them.`
              : 'Photos already on this job.')}
        </DialogDescription>
      </DialogHeader>

      <Input
        placeholder="Search photos…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {!searching && (
        <JobFolderNavigator
          jobId={jobId}
          folderId={folderId}
          onNavigate={setFolderId}
          fileCount={files.length}
        />
      )}

      <div className="max-h-[55vh] overflow-y-auto">
        {isLoading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading photos…</p>
        ) : files.length === 0 && folderId === null && !searching ? (
          <p className="py-8 text-center text-sm text-gray-500">
            No photos on this job yet. Upload them from the job's Files tab, or from a phone on
            site, and they'll show up here.
          </p>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No photos match “{search}”.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {visible.map((file) => {
              const order = pickedIndex(file.id);
              const selected = order !== -1;
              // Every consumer of a pick renders a picture; anything else is
              // shown so the folder doesn't look emptier than it is, but can't
              // be chosen.
              const selectable = isImageEntry(file);
              return (
                <button
                  key={file.id}
                  type="button"
                  aria-pressed={selected}
                  aria-label={file.fileName}
                  disabled={!selectable || (!selected && atCapacity)}
                  title={selectable ? undefined : 'Only photos can be chosen here.'}
                  onClick={() => toggle(file)}
                  className={cn(
                    'group relative overflow-hidden rounded-lg border-2 text-left disabled:opacity-40',
                    selected ? 'border-blue-500' : 'border-transparent hover:border-gray-300',
                  )}
                >
                  <div className="aspect-square w-full">
                    <JobFileThumbnail jobId={jobId} file={file} />
                  </div>

                  {selected && (
                    <span className="absolute right-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white">
                      {/* The number is what says which slot it lands in; a lone
                          tick would leave a four-photo fill ambiguous. */}
                      {maxSelection > 1 ? order + 1 : <Check className="h-3.5 w-3.5" />}
                    </span>
                  )}

                  {usedLabel && isUsed?.(file) && (
                    <span className="absolute left-1.5 top-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                      {usedLabel}
                    </span>
                  )}

                  <p className="truncate px-2 py-1 text-xs text-gray-700" title={file.fileName}>
                    {file.fileName}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <DialogFooter className="sm:justify-between">
        <span className="self-center text-sm text-gray-500">
          {/* Shown for a single slot too, once something is picked. A pick made
              in another folder disables every tile in this one; without a
              count on screen there is nothing to explain why. */}
          {maxSelection > 1
            ? `${picked.length} of ${maxSelection} selected`
            : picked.length > 0
              ? '1 selected'
              : ''}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" disabled={picked.length === 0} onClick={() => onConfirm(picked)}>
            Add {picked.length || ''} {picked.length === 1 ? 'photo' : 'photos'}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
